// ── useConversationDetail ──────────────────────────────────────────
//
// Fetches the full detail for a single conversation (header + last 50
// messages + notes + session) and keeps it fresh via realtime.
//
// Selective realtime handling:
//   • wa:message:new for THIS conversation → refetch
//   • wa:message:status for THIS conversation → refetch (cheaper than
//     surgically patching the cache since we don't have React Query)
//   • wa:note:added for THIS conversation → refetch
//   • wa:conversation:* for THIS conversation → refetch

import { useCallback, useEffect, useRef, useState } from 'react';
import { realtime } from '@/lib/realtime';
import {
  getConversation,
  type ConversationDetailResponse,
} from '../api/whatsapp.api';

const REFETCH_DEBOUNCE_MS = 400;

export function useConversationDetail(conversationId: string | null) {
  const [detail, setDetail] = useState<ConversationDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  const fetchNow = useCallback(async () => {
    if (!conversationId) {
      setDetail(null);
      return;
    }
    setError(null);
    try {
      const res = await getConversation(conversationId);
      setDetail(res);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) {
      setDetail(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchNow();
  }, [conversationId, fetchNow]);

  useEffect(() => {
    if (!conversationId) return;
    const off = realtime.subscribeAll((evt) => {
      const targetId =
        (evt.data as any)?.conversationId ?? (evt.data as any)?.id ?? null;
      // Only refetch when the event concerns THIS conversation. Message
      // status events for outbound messages also include conversationId.
      if (targetId !== conversationId) return;

      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => fetchNow(), REFETCH_DEBOUNCE_MS);
    });
    return () => {
      off();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [conversationId, fetchNow]);

  return { detail, loading, error, refresh: fetchNow };
}
